import React, { ReactNode } from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Folder, GraduationCap } from "lucide-react";
import { ENTITY_ICON } from "../../entityIcons";
import { formatActivityTypeLabel } from "../../AddActivity/config/activityTypes";
import { ProgressNode } from "../types";
import { countDescendants } from "../utils";
import ProgressStatusBadge from "./ProgressStatusBadge";

interface ProgressTreeNodeProps {
  node: ProgressNode;
  renderLeading?: (node: ProgressNode) => ReactNode;
  renderTrailing?: (node: ProgressNode) => ReactNode;
  renderFooter?: (node: ProgressNode) => ReactNode;
  isHighlighted?: (node: ProgressNode) => boolean;
  isDimmed?: (node: ProgressNode) => boolean;
  isMuted?: (node: ProgressNode) => boolean;
}

const ProgressTreeNode: React.FC<ProgressTreeNodeProps> = ({
  node,
  renderLeading,
  renderTrailing,
  renderFooter,
  isHighlighted,
  isDimmed,
  isMuted,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const hasChildren = node.children.length > 0;
  const counts = countDescendants(node);
  const footer = renderFooter?.(node);
  const leading = renderLeading?.(node);
  const mutedSx = { opacity: isMuted?.(node) ? 0.5 : 1 };

  const subtitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          padding: "16px 20px",
          borderRadius: "12px",
          border: `1px solid ${
            isHighlighted?.(node) ? theme.palette.text.primary : "transparent"
          }`,
          backgroundColor: theme.palette.background.card,
          color: theme.palette.text.primary,
          opacity: isDimmed?.(node) ? 0.5 : 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {leading && (
            <Box sx={{ display: "flex", flexShrink: 0, ...mutedSx }}>
              {leading}
            </Box>
          )}
          <Box sx={{ display: "flex", flexShrink: 0, ...mutedSx }}>
            {ENTITY_ICON[node.entityType]}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, ...mutedSx }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {node.title}
            </Typography>
            {node.entityType === "activity" ? (
              <Typography sx={subtitleSx}>
                {formatActivityTypeLabel(node.category)}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" sx={subtitleSx}>
                  <GraduationCap size={12} />
                  {t("counts.activities", { count: counts.activities })}
                </Typography>
                {counts.courses > 0 && (
                  <Typography component="span" sx={subtitleSx}>
                    <Folder size={12} />
                    {t("counts.courses", { count: counts.courses })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {renderTrailing?.(node)}
          <Box sx={{ display: "flex", flexShrink: 0, ...mutedSx }}>
            <ProgressStatusBadge source={node} />
          </Box>
        </Box>
        {footer}
      </Box>

      {hasChildren && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              ml: 2.5,
              pl: 2.5,
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            {node.children.map((child) => (
              <ProgressTreeNode
                key={child.key}
                node={child}
                renderLeading={renderLeading}
                renderTrailing={renderTrailing}
                renderFooter={renderFooter}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                isMuted={isMuted}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProgressTreeNode;
